import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["preview", "strikethrough", "checkbox", "radio"]
  static values = { modes: Array }

  declare readonly previewTargets: HTMLElement[]
  declare readonly strikethroughTarget: HTMLElement
  declare readonly checkboxTarget: HTMLInputElement
  declare readonly radioTargets: HTMLInputElement[]
  declare readonly modesValue: string[]
  declare readonly hasStrikethroughTarget: boolean

  // For checkbox panels: toggle dim + strikethrough + checkbox state (triggered by panel click)
  toggle(): void {
    const checkbox = this.checkboxTarget
    checkbox.checked = !checkbox.checked
    this.applyToggleState(checkbox.checked)
  }

  // For checkbox panels: sync preview when checkbox is clicked directly
  checkboxChanged(): void {
    this.applyToggleState(this.checkboxTarget.checked)
  }

  // For the compact-mode panel: sync preview when a radio is selected directly
  selectMode(event: Event): void {
    const selected = (event.target as HTMLInputElement).value
    this.previewTargets.forEach(p => {
      p.classList.toggle("hidden", p.dataset.mode !== selected)
    })
    if (this.hasStrikethroughTarget) {
      this.strikethroughTarget.classList.toggle("hidden", selected !== "none")
    }
  }

  // For the compact-mode panel: cycle through modes, update preview + radio
  cycle(): void {
    const modes = this.modesValue
    const current = this.radioTargets.find(r => r.checked)?.value ?? modes[0]
    const next = modes[(modes.indexOf(current) + 1) % modes.length]

    this.radioTargets.forEach(r => { r.checked = r.value === next })
    this.previewTargets.forEach(p => {
      p.classList.toggle("hidden", p.dataset.mode !== next)
    })

    if (this.hasStrikethroughTarget) {
      this.strikethroughTarget.classList.toggle("hidden", next !== "none")
    }
  }

  private applyToggleState(excluded: boolean): void {
    const preview = this.previewTargets[0]
    preview.classList.toggle("opacity-50", excluded)
    preview.classList.toggle("border-gray-300", excluded)
    preview.classList.toggle("border-secondary-700", !excluded)
    if (this.hasStrikethroughTarget) {
      this.strikethroughTarget.classList.toggle("hidden", !excluded)
    }
  }
}
